import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../utils/AuthContext';
import { 
  getAccommodationById, 
  createAccommodation, 
  updateAccommodation,
  uploadImage,
  testSupabaseConnection,
  Accommodation 
} from '../../utils/supabaseClient';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';

const FormWrapper = styled.div`
  padding: 2rem;
  max-width: 900px;
  margin: 0 auto;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
`;

const FormHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  color: #333;
  margin: 0;
`;

const FormContainer = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #555;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #e1e1e1;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    border-color: #4a90e2;
    outline: none;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #e1e1e1;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    border-color: #4a90e2;
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #e1e1e1;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  transition: border-color 0.2s;

  &:focus {
    border-color: #4a90e2;
    outline: none;
  }
`;

const FullWidth = styled.div`
  grid-column: span 2;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const ErrorMessage = styled.p`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const AmenitiesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-top: 1rem;
`;

const AmenityItem = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const AddAmenityButton = styled.button`
  background-color: #004744;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  margin-top: 1rem;

  &:hover {
    background-color: #004744;
  }
`;

const RemoveAmenityButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.75rem;
  align-self: center;

  &:hover {
    background-color: #c82333;
  }
`;

const ImagesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
`;

const ImageInput = styled.div`
  width: 100%;
  margin-bottom: 0.5rem;
`;

const FileInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const FileInputLabel = styled.label`
  display: inline-block;
  background-color: #004744;
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  transition: background-color 0.2s;
  margin-right: 1rem;
  max-width: 200px;

  &:hover {
    background-color: #004744;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const ImagePreview = styled.div`
  width: 150px;
  height: 100px;
  position: relative;
  margin-bottom: 1rem;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: white;
  color: #dc3545;
  border: 1px solid #dc3545;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  
  &:hover {
    background-color: #dc3545;
    color: white;
  }
`;

const UploadStatusText = styled.span<{ $isError: boolean }>`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: ${props => props.$isError ? '#dc3545' : '#004744'};
`;

type FormValues = {
  title_it: string;
  title_en: string;
  location_it: string;
  location_en: string;
  price_it: string;
  price_en: string;
  image: string;
  images: string[];
  max_guests: number;
  bedrooms: number;
  bathrooms: number;
  size: string;
  type: string;
  description_it: string;
  description_en: string;
  amenities: {it: string, en: string}[];
  latitude: number;
  longitude: number;
  terrace: boolean;
};

const AccommodationForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [loading, setLoading] = useState(id ? true : false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{
    isUploading: boolean;
    message: string;
    isError: boolean;
  }>({
    isUploading: false,
    message: '',
    isError: false
  });
  
  // File input refs
  const mainImageInputRef = useRef<HTMLInputElement>(null);
  const additionalImageInputRef = useRef<HTMLInputElement>(null);
  
  // Track images being uploaded
  const [tempImage, setTempImage] = useState<File | null>(null);
  const [tempAdditionalImages, setTempAdditionalImages] = useState<File[]>([]);
  
  const { 
    register, 
    handleSubmit, 
    control,
    setValue, 
    watch,
    formState: { errors } 
  } = useForm<FormValues>({
    defaultValues: {
      title_it: '',
      title_en: '',
      location_it: '',
      location_en: '',
      price_it: '',
      price_en: '',
      image: '',
      images: [''],
      max_guests: 1,
      bedrooms: 1,
      bathrooms: 1,
      size: '',
      type: 'apartment',
      description_it: '',
      description_en: '',
      amenities: [{ it: '', en: '' }],
      latitude: 43.7228,  // Default: Pisa
      longitude: 10.4017,  // Default: Pisa
      terrace: false
    }
  });
  
  const amenities = watch('amenities') || [];
  const images = watch('images') || [];
  const mainImage = watch('image');

  useEffect(() => {
    const fetchAccommodation = async () => {
      if (id) {
        const accommodation = await getAccommodationById(id);
        if (accommodation) {
          setValue('title_it', accommodation.title_it);
          setValue('title_en', accommodation.title_en);
          setValue('location_it', accommodation.location_it);
          setValue('location_en', accommodation.location_en);
          setValue('price_it', accommodation.price_it);
          setValue('price_en', accommodation.price_en);
          setValue('image', accommodation.image);
          setValue('images', accommodation.images);
          setValue('max_guests', accommodation.max_guests);
          setValue('bedrooms', accommodation.bedrooms);
          setValue('bathrooms', accommodation.bathrooms);
          setValue('size', accommodation.size);
          setValue('type', accommodation.type);
          setValue('description_it', accommodation.description_it);
          setValue('description_en', accommodation.description_en);
          setValue('amenities', accommodation.amenities);
          setValue('latitude', accommodation.latitude || 43.7228);  // Default: Pisa
          setValue('longitude', accommodation.longitude || 10.4017);  // Default: Pisa
          setValue('terrace', accommodation.terrace || false);
        }
      }
      setLoading(false);
    };

    fetchAccommodation();
  }, [id, setValue]);

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setTempImage(file);
  };
  
  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const newFiles = Array.from(files);
    setTempAdditionalImages(prev => [...prev, ...newFiles]);
  };
  
  const handleUploadMainImage = async () => {
    if (!tempImage) return;
    
    // Check authentication first
    if (!user || !session) {
      setUploadStatus({
        isUploading: false,
        message: 'You must be logged in to upload images. Please refresh and log in again.',
        isError: true
      });
      return;
    }
    
    try {
      setUploadStatus({
        isUploading: true,
        message: 'Uploading main image...',
        isError: false
      });
      
      console.log('Starting main image upload...');
      console.log('User:', user.email, 'Session exists:', !!session);
      const imageUrl = await uploadImage(tempImage);
      
      if (imageUrl) {
        setValue('image', imageUrl);
        setTempImage(null);
        setUploadStatus({
          isUploading: false,
          message: 'Main image uploaded successfully',
          isError: false
        });
        
        // Reset file input
        if (mainImageInputRef.current) {
          mainImageInputRef.current.value = '';
        }
        
        // Clear the success message after 3 seconds
        setTimeout(() => {
          setUploadStatus({
            isUploading: false,
            message: '',
            isError: false
          });
        }, 3000);
      } else {
        throw new Error('Failed to upload image - check console for details');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadStatus({
        isUploading: false,
        message: `Error uploading image: ${error.message || 'Unknown error'}`,
        isError: true
      });
    }
  };
  
  const handleUploadAdditionalImages = async () => {
    if (tempAdditionalImages.length === 0) return;
    
    // Check authentication first
    if (!user || !session) {
      setUploadStatus({
        isUploading: false,
        message: 'You must be logged in to upload images. Please refresh and log in again.',
        isError: true
      });
      return;
    }
    
    try {
      setUploadStatus({
        isUploading: true,
        message: `Uploading ${tempAdditionalImages.length} additional images...`,
        isError: false
      });
      
      console.log('Starting additional images upload...');
      console.log('User:', user.email, 'Session exists:', !!session);
      const uploadPromises = tempAdditionalImages.map(file => uploadImage(file));
      const uploadResults = await Promise.all(uploadPromises);
      const validUrls = uploadResults.filter(url => url !== null) as string[];
      
      if (validUrls.length > 0) {
        // Add to existing images
        const currentImages = (watch('images') || []).filter(img => img.trim() !== '');
        setValue('images', [...currentImages, ...validUrls]);
        setTempAdditionalImages([]);
        
        const successMessage = validUrls.length === tempAdditionalImages.length 
          ? `${validUrls.length} images uploaded successfully`
          : `${validUrls.length} of ${tempAdditionalImages.length} images uploaded successfully`;
          
        setUploadStatus({
          isUploading: false,
          message: successMessage,
          isError: false
        });
        
        // Reset file input
        if (additionalImageInputRef.current) {
          additionalImageInputRef.current.value = '';
        }
        
        // Clear the success message after 3 seconds
        setTimeout(() => {
          setUploadStatus({
            isUploading: false,
            message: '',
            isError: false
          });
        }, 3000);
      } else {
        throw new Error('Failed to upload any images - check console for details');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadStatus({
        isUploading: false,
        message: `Error uploading images: ${error.message || 'Unknown error'}`,
        isError: true
      });
    }
  };
  
  const removeTemporaryImage = () => {
    setTempImage(null);
    if (mainImageInputRef.current) {
      mainImageInputRef.current.value = '';
    }
  };
  
  const removeTemporaryAdditionalImage = (index: number) => {
    setTempAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      // First upload the main image if there's a temporary one
      if (tempImage) {
        const mainImageUrl = await uploadImage(tempImage);
        if (mainImageUrl) {
          data.image = mainImageUrl;
        } else {
          throw new Error('Failed to upload main image');
        }
      }
      
      // Then upload any additional images
      if (tempAdditionalImages.length > 0) {
        const uploadPromises = tempAdditionalImages.map(file => uploadImage(file));
        const uploadResults = await Promise.all(uploadPromises);
        const validUrls = uploadResults.filter(url => url !== null) as string[];
        
        // Add to existing images
        if (validUrls.length > 0) {
          const currentImages = data.images.filter(img => img.trim() !== '');
          data.images = [...currentImages, ...validUrls];
        }
      }

      // Filter out any empty image URLs
      data.images = data.images.filter(img => img.trim() !== '');
      
      // If no images are provided, use the main image
      if (data.images.length === 0 && data.image) {
        data.images = [data.image];
      }

      // Filter out empty amenities
      data.amenities = data.amenities.filter(amenity => 
        amenity.it.trim() !== '' || amenity.en.trim() !== ''
      );

      // Validate main image
      if (!data.image || data.image.trim() === '') {
        setError('Main image is required');
        setIsSubmitting(false);
        return;
      }

      if (id) {
        // Update existing accommodation
        const updated = await updateAccommodation(id, data);
        if (updated) {
          navigate('/admin/dashboard');
        } else {
          setError('Failed to update accommodation');
        }
      } else {
        // Create new accommodation
        const created = await createAccommodation(data);
        if (created) {
          navigate('/admin/dashboard');
        } else {
          setError('Failed to create accommodation');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddAmenity = () => {
    const currentAmenities = [...amenities];
    currentAmenities.push({ it: '', en: '' });
    setValue('amenities', currentAmenities);
  };

  const handleRemoveAmenity = (index: number) => {
    const currentAmenities = [...amenities];
    currentAmenities.splice(index, 1);
    setValue('amenities', currentAmenities);
  };

  const handleRemoveImage = (index: number) => {
    const currentImages = [...images];
    // Find the actual index in the full array by mapping filtered to original indices
    const filteredImages = currentImages.filter(img => img.trim() !== '');
    const actualIndex = currentImages.indexOf(filteredImages[index]);
    if (actualIndex !== -1) {
      currentImages.splice(actualIndex, 1);
      setValue('images', currentImages);
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  const handleTestConnection = async () => {
    console.log('=== TESTING SUPABASE CONNECTION ===');
    setUploadStatus({
      isUploading: true,
      message: 'Testing connection...',
      isError: false
    });

    const result = await testSupabaseConnection();
    
    if (result.connection) {
      setUploadStatus({
        isUploading: false,
        message: 'Connection test completed - check console for details',
        isError: false
      });
    } else {
      setUploadStatus({
        isUploading: false,
        message: 'Connection test failed - check console for details',
        isError: true
      });
    }

    setTimeout(() => {
      setUploadStatus({
        isUploading: false,
        message: '',
        isError: false
      });
    }, 5000);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <FormWrapper>
      <FormHeader>
        <Title>{id ? 'Edit Accommodation' : 'Add New Accommodation'}</Title>
        <div style={{ fontSize: '0.9rem', color: user ? '#004744' : '#dc3545' }}>
          {user ? `✓ Logged in as: ${user.email}` : '⚠️ Not authenticated'}
        </div>
      </FormHeader>

      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label htmlFor="title_it">Title (Italian)</Label>
          <Input
            id="title_it"
            {...register('title_it', { required: 'Title in Italian is required' })}
          />
          {errors.title_it && <ErrorMessage>{errors.title_it.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="title_en">Title (English)</Label>
          <Input
            id="title_en"
            {...register('title_en', { required: 'Title in English is required' })}
          />
          {errors.title_en && <ErrorMessage>{errors.title_en.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="location_it">Location (Italian)</Label>
          <Input
            id="location_it"
            {...register('location_it', { required: 'Location in Italian is required' })}
          />
          {errors.location_it && <ErrorMessage>{errors.location_it.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="location_en">Location (English)</Label>
          <Input
            id="location_en"
            {...register('location_en', { required: 'Location in English is required' })}
          />
          {errors.location_en && <ErrorMessage>{errors.location_en.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="price_it">Price (Italian)</Label>
          <Input
            id="price_it"
            placeholder="€150/notte"
            {...register('price_it', { required: 'Price in Italian is required' })}
          />
          {errors.price_it && <ErrorMessage>{errors.price_it.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="price_en">Price (English)</Label>
          <Input
            id="price_en"
            placeholder="€150/night"
            {...register('price_en', { required: 'Price in English is required' })}
          />
          {errors.price_en && <ErrorMessage>{errors.price_en.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="max_guests">Number of Guests</Label>
          <Input
            id="max_guests"
            type="number"
            min="0"
            {...register('max_guests', { 
              required: 'Number of guests is required',
              valueAsNumber: true
            })}
          />
          {errors.max_guests && <ErrorMessage>{errors.max_guests.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="terrace">Terrace</Label>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
            <input
              id="terrace"
              type="checkbox"
              style={{ width: '20px', height: '20px', marginRight: '8px' }}
              {...register('terrace')}
            />
            <span>Property has terrace</span>
          </div>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="bedrooms">Number of Bedrooms</Label>
          <Input
            id="bedrooms"
            type="number"
            min="0"
            {...register('bedrooms', { 
              required: 'Number of bedrooms is required',
              valueAsNumber: true
            })}
          />
          {errors.bedrooms && <ErrorMessage>{errors.bedrooms.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="bathrooms">Number of Bathrooms</Label>
          <Input
            id="bathrooms"
            type="number"
            min="0"
            {...register('bathrooms', { 
              required: 'Number of bathrooms is required',
              valueAsNumber: true
            })}
          />
          {errors.bathrooms && <ErrorMessage>{errors.bathrooms.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="size">Size</Label>
          <Input
            id="size"
            placeholder="120 m²"
            {...register('size', { required: 'Size is required' })}
          />
          {errors.size && <ErrorMessage>{errors.size.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="type">Property Type</Label>
          <Select
            id="type"
            {...register('type', { required: 'Property type is required' })}
          >
            <option value="apartment">Apartment</option>
            <option value="villa">Villa</option>
            <option value="house">House</option>
            <option value="studio">Studio</option>
          </Select>
          {errors.type && <ErrorMessage>{errors.type.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="latitude">Latitude</Label>
          <Input
            id="latitude"
            type="number"
            step="any"
            placeholder="43.7228"
            {...register('latitude', { 
              required: 'Latitude is required',
              valueAsNumber: true
            })}
          />
          {errors.latitude && <ErrorMessage>{errors.latitude.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="longitude">Longitude</Label>
          <Input
            id="longitude"
            type="number"
            step="any"
            placeholder="10.4017"
            {...register('longitude', { 
              required: 'Longitude is required',
              valueAsNumber: true
            })}
          />
          {errors.longitude && <ErrorMessage>{errors.longitude.message}</ErrorMessage>}
        </FormGroup>

        <FullWidth>
          <FormGroup>
            <Label>Main Image</Label>
            <div style={{ marginBottom: '1rem' }}>
              {mainImage && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Current Image URL:</strong>
                  <div>
                    <a href={mainImage} target="_blank" rel="noopener noreferrer">
                      {mainImage}
                    </a>
                  </div>
                  <div style={{ marginTop: '0.5rem' }}>
                    <img 
                      src={mainImage} 
                      alt="Main" 
                      style={{ 
                        width: '150px', 
                        height: '100px', 
                        objectFit: 'cover', 
                        borderRadius: '4px' 
                      }} 
                    />
                  </div>
                </div>
              )}
              <FileInputContainer>
                <FileInputLabel htmlFor="mainImageFile">
                  {mainImage ? 'Change Main Image' : 'Upload Main Image'}
                </FileInputLabel>
                <FileInput
                  id="mainImageFile"
                  type="file"
                  accept="image/*"
                  onChange={handleMainImageChange}
                  ref={mainImageInputRef}
                />
                {/* Hidden input for form validation */}
                <input
                  type="hidden"
                  {...register('image', { required: 'Main image is required' })}
                />
                
                {/* Test Connection Button */}
                <div style={{ marginTop: '1rem' }}>
                  <Button 
                    type="button" 
                    onClick={handleTestConnection}
                    variant="outline"
                    style={{ backgroundColor: '#6c757d', color: 'white', borderColor: '#6c757d', marginRight: '1rem' }}
                  >
                    Test Connection
                  </Button>
                  <small style={{ color: '#666' }}>Click to test Supabase connection if uploads fail</small>
                </div>

                {tempImage && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '1rem' }}>
                      <Button 
                        type="button" 
                        onClick={handleUploadMainImage}
                        disabled={uploadStatus.isUploading}
                        variant="outline"
                        style={{ marginRight: '1rem' }}
                      >
                        {uploadStatus.isUploading ? 'Uploading...' : 'Upload This Image'}
                      </Button>
                      <span>{tempImage.name}</span>
                      <RemoveImageButton 
                        type="button" 
                        onClick={removeTemporaryImage}
                        style={{ position: 'relative', marginLeft: '0.5rem' }}
                      >
                        ×
                      </RemoveImageButton>
                    </div>
                    <ImagePreviewContainer>
                      <ImagePreview>
                        <PreviewImage src={URL.createObjectURL(tempImage)} alt="Preview" />
                      </ImagePreview>
                    </ImagePreviewContainer>
                  </>
                )}
              </FileInputContainer>
              {errors.image && <ErrorMessage>{errors.image.message}</ErrorMessage>}
            </div>
          </FormGroup>
        </FullWidth>

        <FullWidth>
          <FormGroup>
            <Label>Additional Images</Label>
            <FileInputContainer>
              <FileInputLabel htmlFor="additionalImageFiles">
                Upload Additional Images (Multiple Selection)
              </FileInputLabel>
              <FileInput
                id="additionalImageFiles"
                type="file"
                accept="image/*"
                multiple
                onChange={handleAdditionalImagesChange}
                ref={additionalImageInputRef}
              />
              {tempAdditionalImages.length > 0 && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '1rem' }}>
                    <Button 
                      type="button" 
                      onClick={handleUploadAdditionalImages}
                      disabled={uploadStatus.isUploading}
                      variant="outline"
                      style={{ marginRight: '1rem' }}
                    >
                      {uploadStatus.isUploading ? 'Uploading...' : `Upload ${tempAdditionalImages.length} Images`}
                    </Button>
                    <span>{tempAdditionalImages.length} files selected</span>
                  </div>
                  <ImagePreviewContainer>
                    {tempAdditionalImages.map((file, index) => (
                      <ImagePreview key={index}>
                        <PreviewImage src={URL.createObjectURL(file)} alt={`Preview ${index + 1}`} />
                        <RemoveImageButton 
                          type="button" 
                          onClick={() => removeTemporaryAdditionalImage(index)}
                        >
                          ×
                        </RemoveImageButton>
                      </ImagePreview>
                    ))}
                  </ImagePreviewContainer>
                </>
              )}
            </FileInputContainer>
            
            {uploadStatus.message && (
              <UploadStatusText $isError={uploadStatus.isError}>
                {uploadStatus.message}
              </UploadStatusText>
            )}
            
            {images.length > 0 && (
              <>
                <h4 style={{ marginTop: '1rem' }}>Current Additional Images</h4>
                <ImagePreviewContainer>
                  {images.filter(img => img.trim() !== '').map((imageUrl, index) => (
                    <ImagePreview key={index}>
                      <PreviewImage src={imageUrl} alt={`Current Image ${index + 1}`} />
                      <RemoveImageButton 
                        type="button" 
                        onClick={() => handleRemoveImage(index)}
                      >
                        ×
                      </RemoveImageButton>
                    </ImagePreview>
                  ))}
                </ImagePreviewContainer>
              </>
            )}
          </FormGroup>
        </FullWidth>

        <FullWidth>
          <FormGroup>
            <Label htmlFor="description_it">Description (Italian)</Label>
            <TextArea
              id="description_it"
              {...register('description_it', { required: 'Description in Italian is required' })}
            />
            {errors.description_it && <ErrorMessage>{errors.description_it.message}</ErrorMessage>}
          </FormGroup>
        </FullWidth>

        <FullWidth>
          <FormGroup>
            <Label htmlFor="description_en">Description (English)</Label>
            <TextArea
              id="description_en"
              {...register('description_en', { required: 'Description in English is required' })}
            />
            {errors.description_en && <ErrorMessage>{errors.description_en.message}</ErrorMessage>}
          </FormGroup>
        </FullWidth>

        <FullWidth>
          <FormGroup>
            <Label>Amenities</Label>
            <AmenitiesContainer>
              {amenities.map((_, index) => (
                <AmenityItem key={index}>
                  <div style={{ flex: 1 }}>
                    <Input
                      placeholder="Amenity (Italian)"
                      {...register(`amenities.${index}.it` as const)}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    <Input
                      placeholder="Amenity (English)"
                      {...register(`amenities.${index}.en` as const)}
                    />
                  </div>
                  <RemoveAmenityButton 
                    type="button" 
                    onClick={() => handleRemoveAmenity(index)}
                  >
                    Remove
                  </RemoveAmenityButton>
                </AmenityItem>
              ))}
            </AmenitiesContainer>
            <AddAmenityButton type="button" onClick={handleAddAmenity}>
              Add Amenity
            </AddAmenityButton>
          </FormGroup>
        </FullWidth>

        {error && (
          <FullWidth>
            <ErrorMessage>{error}</ErrorMessage>
          </FullWidth>
        )}

        <FullWidth>
          <ButtonContainer>
            <Button 
              type="button" 
              onClick={handleCancel}
              variant="outline"
              style={{ backgroundColor: '#6c757d', color: 'white', borderColor: '#6c757d' }}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isSubmitting}
              variant="outline"
              style={{ minWidth: '120px' }}
            >
              {isSubmitting ? 'Saving...' : id ? 'Update' : 'Create'}
            </Button>
          </ButtonContainer>
        </FullWidth>
      </FormContainer>
    </FormWrapper>
  );
};

export default AccommodationForm; 